import { useEffect } from 'react'

import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes'

import { CloseCircle } from 'entities/close-circle/model'
import userStore from 'entities/user/module/user-store'

import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'

import { useInsuranceDraft } from '../../../../model/insurance-draft'
import {
  createEmptyInsuranceParticipant,
  createEmptyInsurancePolicyholder,
  prepareSelfInsurancePolicyholder,
  resizeInsuranceParticipants,
} from '../../../../model/policyholder'
import { InsuranceCitizenship } from '../../../../model/types'
import { InsuranceLayout } from '../../../../ui/insurance-layout'
import {
  PolicyholderFormValues,
  policyholderFormResolver,
} from '../../lib/policyholder-form'
import { ParticipantCard } from '../participant-card'
import { PolicyholderCard } from '../policyholder-card'

import { CloseCircleDialog } from './ui/close-circle-dialog'

import {
  getCloseCircleCitizenship,
  getCloseCirclePersonData,
} from './lib/close-circle'
import {
  type PolicyholderValidationState,
  getValidationTarget,
} from './lib/get-validation-target'
import s from './styles.module.scss'

const FORM_ID = 'insurance-policyholder-form'
const POLICYHOLDER_CARD_ID = 'insurance-policyholder-card'
const getParticipantCardId = (index: number) =>
  `insurance-participant-card-${index}`

interface PolicyholderFormProps {
  citizenships: InsuranceCitizenship[]
  closeCircles: CloseCircle[] | null
  loadCloseCircles: () => Promise<CloseCircle[] | null>
}

export const PolicyholderForm = ({
  citizenships,
  closeCircles,
  loadCloseCircles,
}: PolicyholderFormProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { draft, setPolicyholderData } = useInsuranceDraft()
  const { closeDialog, openDialog } = useDialogBottomSheet()
  const totalPeople = draft.travelerAges.length + draft.infantAges.length
  const maxParticipants = Math.max(0, totalPeople - 1)

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<PolicyholderFormValues>({
    defaultValues: {
      policyholder:
        draft.policyholder ||
        (draft.policyholderKind === 'self'
          ? prepareSelfInsurancePolicyholder(userStore.account, citizenships)
          : createEmptyInsurancePolicyholder()),
      participants: resizeInsuranceParticipants(
        draft.participants,
        maxParticipants,
      ),
    },
    resolver: policyholderFormResolver,
    mode: 'onChange',
  })

  const returnState = location.state as PolicyholderValidationState | null
  const validationTarget = getValidationTarget(
    returnState,
    draft.policyholderKind === 'self' && !draft.policyholder,
  )
  const returnCardId =
    returnState?.person === 'participant' &&
    typeof returnState.participantIndex === 'number'
      ? getParticipantCardId(returnState.participantIndex)
      : returnState?.person === 'policyholder'
        ? POLICYHOLDER_CARD_ID
        : null

  useEffect(() => {
    if (!validationTarget) return

    let frameId: number | undefined

    const validateAndRestorePosition = async () => {
      await trigger(validationTarget)

      if (!returnCardId) return

      frameId = requestAnimationFrame(() => {
        document.getElementById(returnCardId)?.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        })
        navigate(location.pathname, { replace: true, state: null })
      })
    }

    void validateAndRestorePosition()

    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId)
    }
  }, [location.pathname, navigate, returnCardId, trigger, validationTarget])

  useEffect(() => {
    if (closeCircles === null) void loadCloseCircles()
  }, [closeCircles, loadCloseCircles])

  const onSubmit = handleSubmit((values) => {
    setPolicyholderData(values.policyholder, values.participants)

    // eslint-disable-next-line no-console
    console.info('Insurance policyholder data', values)

    navigate(RoutingPaths.insurance.calculation.path())
  })

  const onScanPassport = (
    person: 'policyholder' | 'participant',
    index?: number,
  ) => {
    setPolicyholderData(getValues('policyholder'), getValues('participants'))

    navigate({
      pathname: RoutingPaths.insurance.scanPassport.path(),
      search: createSearchParams({
        person,
        participant: index === undefined ? '' : String(index),
      }).toString(),
    })
  }

  const onSelectCitizenship = (
    person: 'policyholder' | 'participant',
    index?: number,
  ) => {
    setPolicyholderData(getValues('policyholder'), getValues('participants'))

    navigate(
      {
        pathname: RoutingPaths.insurance.citizenship.path(),
        search: createSearchParams({
          person,
          participant: index === undefined ? '' : String(index),
        }).toString(),
      },
      { replace: true },
    )
  }

  const onSelectCloseCircle = (
    closeCircle: CloseCircle,
    person: 'policyholder' | 'participant',
    index?: number,
  ) => {
    const personData = getCloseCirclePersonData(closeCircle)

    if (person === 'policyholder') {
      setValue(
        'policyholder',
        {
          ...createEmptyInsurancePolicyholder(),
          ...personData,
          citizenship:
            getCloseCircleCitizenship(closeCircle, citizenships) || null,
        },
        { shouldValidate: true },
      )
    }

    if (person === 'participant' && index !== undefined) {
      setValue(
        `participants.${index}`,
        {
          ...createEmptyInsuranceParticipant(),
          ...personData,
          citizenship:
            getCloseCircleCitizenship(closeCircle, citizenships) || null,
        },
        { shouldValidate: true },
      )
    }

    closeDialog()
  }

  const onShowCloseCircle = (
    person: 'policyholder' | 'participant',
    index?: number,
  ) => {
    openDialog({
      child: (
        <CloseCircleDialog
          closeCircles={closeCircles || []}
          onSelect={(closeCircle) =>
            onSelectCloseCircle(closeCircle, person, index)
          }
        />
      ),
    })
  }

  return (
    <InsuranceLayout
      title={t('insurance.vzr.policyholder.title')}
      onGoBack={() => navigate(-1)}
      footer={{
        type: 'submit',
        form: FORM_ID,
        disabled: !isValid,
        children: t('insurance.vzr.actions.continue'),
      }}
    >
      <form
        id={FORM_ID}
        className={s.form}
        noValidate
        onSubmit={onSubmit}
      >
        <div id={POLICYHOLDER_CARD_ID}>
          <PolicyholderCard
            control={control}
            errors={errors.policyholder}
            isSelf={draft.policyholderKind === 'self'}
            onSelectCitizenship={() => onSelectCitizenship('policyholder')}
            onCloseCircle={() => onShowCloseCircle('policyholder')}
            onScanPassport={() => onScanPassport('policyholder')}
          />
        </div>

        {Array.from({ length: maxParticipants }, (_, index) => (
          <div
            key={index}
            id={getParticipantCardId(index)}
          >
            <ParticipantCard
              index={index}
              control={control}
              errors={errors.participants?.[index]}
              onSelectCitizenship={() =>
                onSelectCitizenship('participant', index)
              }
              onCloseCircle={() => onShowCloseCircle('participant', index)}
              onScanPassport={() => onScanPassport('participant', index)}
            />
          </div>
        ))}
      </form>
    </InsuranceLayout>
  )
}
